import React, { useState } from 'react';
import { Employee, WeeklySchedule, EmployeeStatusEnum, TimeSlot, STATUS_CONFIG } from '../types';

interface DayEmployeeStatus {
    employee: Employee;
    status: EmployeeStatusEnum;
    timeSlots: TimeSlot[];
}

interface CalendarDayProps {
    date: Date;
    isCurrentMonth: boolean;
    isToday: boolean;
    employees: Employee[];
    dayData: DayEmployeeStatus[];
    onDayClick: (date: Date) => void;
}

interface MonthlyCalendarProps {
    currentMonth: Date;
    employees: Employee[];
    schedules: WeeklySchedule[];
    onScheduleUpdate?: (employeeId: string, date: string, status: EmployeeStatusEnum, timeSlots: TimeSlot[]) => void;
}

const CalendarDay: React.FC<CalendarDayProps> = ({
    date,
    isCurrentMonth,
    isToday,
    employees,
    dayData,
    onDayClick
}) => {
    const [showDetails, setShowDetails] = useState(false);

    const getEmployeeStatus = (employeeId: string): DayEmployeeStatus => {
        return dayData.find(d => d.employee.id === employeeId) || {
            employee: employees.find(e => e.id === employeeId)!,
            status: EmployeeStatusEnum.DEFAULT,
            timeSlots: []
        };
    };

    const getStatusCounts = () => {
        const counts = {
            [EmployeeStatusEnum.PRESENT]: 0,
            [EmployeeStatusEnum.ABSENT]: 0,
            [EmployeeStatusEnum.VACATION]: 0,
            [EmployeeStatusEnum.HOLIDAY]: 0,
            [EmployeeStatusEnum.SHOP_CLOSED]: 0,
            [EmployeeStatusEnum.DEFAULT]: 0,
        };

        dayData.forEach(d => {
            counts[d.status]++;
        });

        return counts;
    };

    const getTotalHours = (): number => {
        return dayData.reduce((total, d) => {
            if (d.status === EmployeeStatusEnum.PRESENT) {
                return total + d.timeSlots.reduce((slotTotal, slot) => {
                    const startMinutes = slot.startTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
                    const endMinutes = slot.endTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
                    return slotTotal + (endMinutes - startMinutes) / 60;
                }, 0);
            }
            return total;
        }, 0);
    };

    const counts = getStatusCounts();
    const totalHours = getTotalHours();

    return (
        <div
            className={`relative min-h-[100px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors
                ${!isCurrentMonth ? 'bg-gray-100 text-gray-400' : 'bg-white'}
                ${isToday ? 'ring-2 ring-blue-400 bg-blue-50' : ''}
            `}
            onClick={() => onDayClick(date)}
            onMouseEnter={() => setShowDetails(true)}
            onMouseLeave={() => setShowDetails(false)}
        >
            {/* Date */}
            <div className={`text-sm font-medium mb-2 ${isToday ? 'text-blue-700' : ''}`}>
                {date.getDate()}
            </div>

            {/* Status indicators */}
            {isCurrentMonth && (
                <div className="space-y-1">
                    {counts[EmployeeStatusEnum.PRESENT] > 0 && (
                        <div className="flex items-center text-xs">
                            <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                            <span className="text-green-700">{counts[EmployeeStatusEnum.PRESENT]}</span>
                        </div>
                    )}
                    {counts[EmployeeStatusEnum.ABSENT] > 0 && (
                        <div className="flex items-center text-xs">
                            <div className="w-2 h-2 bg-red-400 rounded-full mr-1"></div>
                            <span className="text-red-700">{counts[EmployeeStatusEnum.ABSENT]}</span>
                        </div>
                    )}
                    {counts[EmployeeStatusEnum.VACATION] > 0 && (
                        <div className="flex items-center text-xs">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-1"></div>
                            <span className="text-blue-700">{counts[EmployeeStatusEnum.VACATION]}</span>
                        </div>
                    )}
                    {totalHours > 0 && (
                        <div className="text-xs text-gray-600 font-medium mt-2">
                            {totalHours.toFixed(1)}h
                        </div>
                    )}
                </div>
            )}

            {/* Hover details */}
            {showDetails && isCurrentMonth && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3 min-w-[200px]">
                    <div className="text-sm font-medium text-gray-900 mb-2">
                        {date.toLocaleDateString('fr-FR', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long' 
                        })}
                    </div>
                    <div className="space-y-1">
                        {employees.map(employee => {
                            const empStatus = getEmployeeStatus(employee.id);
                            const config = STATUS_CONFIG[empStatus.status];
                            return (
                                <div key={employee.id} className="flex items-center justify-between text-xs">
                                    <span className="truncate mr-2">
                                        {employee.firstName} {employee.lastName.charAt(0)}.
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs ${config.color}`}>
                                        {config.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    {totalHours > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
                            Total: {totalHours.toFixed(1)} heures
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({
    currentMonth,
    employees,
    schedules
}) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Generate calendar days
    const getCalendarDays = (): Date[] => {
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        
        // Find the first Monday before or on the start of month
        const startDate = new Date(startOfMonth);
        const dayOfWeek = startDate.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startDate.setDate(startDate.getDate() + daysToMonday);
        
        // Generate 6 weeks of days (42 days)
        const days: Date[] = [];
        for (let i = 0; i < 42; i++) {
            const day = new Date(startDate);
            day.setDate(startDate.getDate() + i);
            days.push(day);
        }
        
        return days;
    };

    const getDayData = (date: Date): DayEmployeeStatus[] => {
        const dateKey = date.toISOString().split('T')[0];
        
        return employees.map(employee => {
            const employeeSchedule = schedules.find(s => s.employeeId === employee.id);
            const daySchedule = employeeSchedule?.days[dateKey];
            
            return {
                employee,
                status: daySchedule?.status || EmployeeStatusEnum.DEFAULT,
                timeSlots: daySchedule?.timeSlots || []
            };
        });
    };

    const isToday = (date: Date): boolean => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isCurrentMonth = (date: Date): boolean => {
        return date.getMonth() === currentMonth.getMonth() && 
               date.getFullYear() === currentMonth.getFullYear();
    };

    const handleDayClick = (date: Date) => {
        setSelectedDate(date);
        console.log('Day clicked:', date);
        // Here you could open a day detail modal or navigate to week view
    };

    const calendarDays = getCalendarDays();
    const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header with day names */}
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                {dayNames.map(dayName => (
                    <div key={dayName} className="p-3 text-center text-sm font-medium text-gray-700">
                        {dayName}
                    </div>
                ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7">
                {calendarDays.map((date, index) => (
                    <CalendarDay
                        key={index}
                        date={date}
                        isCurrentMonth={isCurrentMonth(date)}
                        isToday={isToday(date)}
                        employees={employees}
                        dayData={getDayData(date)}
                        onDayClick={handleDayClick}
                    />
                ))}
            </div>
        </div>
    );
};